import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Container,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import {
  parentCourseService,
  type ChildAvailableCoursesResponse,
  type CourseCard,
} from '../services/parentCourseService';
import CourseSelectionStep from '../components/registration/CourseSelectionStep';
import EnrollmentReviewStep from '../components/registration/EnrollmentReviewStep';
import ProfileEditPopup from '../components/registration/ProfileEditPopup';
import {
  pathAfterAbacusEnroll,
  registrationCourseNames,
  selectableCourseIds,
} from '../utils/abacusRegistrationAction';

type PageStep = 'loading' | 'selection' | 'review' | 'success';
const STEP_LABELS = ['Select courses', 'Review', 'Done'];
const STEP_INDEX: Record<PageStep, number> = { loading: 0, selection: 0, review: 1, success: 2 };

const ParentCourseRegistrationPage: React.FC = () => {
  const { childId: childIdParam } = useParams<{ childId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const childId = Number(childIdParam);

  const [pageStep, setPageStep] = useState<PageStep>('loading');
  const [available, setAvailable] = useState<ChildAvailableCoursesResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingCourses, setLoadingCourses] = useState(false);

  const [selectedCourseIds, setSelectedCourseIds] = useState<Set<number>>(new Set());
  const [profilePopupOpen, setProfilePopupOpen] = useState(false);

  const [enrolling, setEnrolling] = useState(false);
  const [enrollError, setEnrollError] = useState<string | null>(null);
  const [successCourses, setSuccessCourses] = useState<{ id: number; name: string }[]>([]);
  const [partialFailures, setPartialFailures] = useState<{ courseId: number; reason: string }[]>([]);
  const [removedCount, setRemovedCount] = useState(0);

  const loadCourses = useCallback(async () => {
    setLoadingCourses(true);
    setLoadError(null);
    try {
      const result = await parentCourseService.getAvailableCourses(childId);
      setAvailable(result);
      setPageStep('selection');
      const preset = Number(searchParams.get('courseId'));
      if (preset && registrationCourseNames(result).has(preset)) {
        setSelectedCourseIds(new Set([preset]));
      }
      if (!result.profileComplete) {
        setProfilePopupOpen(true);
      }
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : 'Failed to load courses');
      setPageStep('selection');
    } finally {
      setLoadingCourses(false);
    }
  }, [childId, searchParams]);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const handleSelectionChange = (courseId: number, selected: boolean) => {
    setSelectedCourseIds((prev) => {
      const next = new Set(prev);
      if (selected) next.add(courseId);
      else next.delete(courseId);
      return next;
    });
  };

  const handleProfileSaved = async () => {
    setProfilePopupOpen(false);
    // Re-load course list after profile update; prune selections that no longer match
    setLoadingCourses(true);
    try {
      const updated = await parentCourseService.getAvailableCourses(childId);
      setAvailable(updated);
      const validIds = selectableCourseIds(updated);
      setSelectedCourseIds((prev) => {
        const pruned = new Set([...prev].filter((id) => validIds.has(id)));
        setRemovedCount(prev.size - pruned.size);
        return pruned;
      });
      setPageStep('selection');
    } catch {
      setPageStep('selection');
    } finally {
      setLoadingCourses(false);
    }
  };

  const handleConfirmEnroll = async () => {
    if (!available) return;
    setEnrolling(true);
    setEnrollError(null);
    try {
      const ids = [...selectedCourseIds];
      const result = await parentCourseService.enrollBatch(childId, ids);

      const names = registrationCourseNames(available);
      setSuccessCourses(
        result.enrolledCourseIds.map((id) => ({ id, name: names.get(id) ?? `Course ${id}` })),
      );
      setPartialFailures(result.failures);
      const nextPath = pathAfterAbacusEnroll(
        available,
        result.enrolledCourseIds,
        result.failures.length,
        childId,
      );
      if (nextPath) {
        navigate(nextPath);
        return;
      }
      setPageStep('success');
    } catch (e: unknown) {
      setEnrollError(e instanceof Error ? e.message : 'Enrollment failed. Please try again.');
    } finally {
      setEnrolling(false);
    }
  };

  const selectedCourses: CourseCard[] = (() => {
    if (!available) {
      return [];
    }
    const names = registrationCourseNames(available);
    return [...selectedCourseIds].flatMap((id) => {
      const curriculum = available.courses.find((course) => course.courseId === id);
      if (curriculum) {
        return [curriculum];
      }
      const name = names.get(id);
      if (!name) {
        return [];
      }
      return [{
        courseId: id,
        name,
        courseType: 'ABACUS_MATHS',
        subject: null,
        curriculumLevel: null,
        description: null,
        imageUrl: null,
      }];
    });
  })();

  if (loadError) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{loadError}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 3 }}>
        Register for courses
      </Typography>

      <Stepper activeStep={STEP_INDEX[pageStep]} sx={{ mb: 4 }}>
        {STEP_LABELS.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Profile completeness popup — shown when profile is incomplete */}
      {available && (
        <ProfileEditPopup
          open={profilePopupOpen}
          childProfile={available.childProfile}
          onSave={handleProfileSaved}
          onClose={() => {
            setProfilePopupOpen(false);
            setPageStep('selection');
          }}
        />
      )}

      {/* Pruned-selections notification */}
      {removedCount > 0 && (
        <Alert severity="info" onClose={() => setRemovedCount(0)} sx={{ mb: 2 }}>
          {removedCount} course{removedCount > 1 ? 's' : ''} removed — no longer available for
          the updated grade.
        </Alert>
      )}

      {pageStep !== 'success' && enrollError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {enrollError}
        </Alert>
      )}

      {/* Course selection step */}
      {(pageStep === 'loading' || pageStep === 'selection') && available && (
        <CourseSelectionStep
          response={available}
          selectedCourseIds={selectedCourseIds}
          loading={loadingCourses}
          childId={childId}
          onSelectionChange={handleSelectionChange}
          onContinue={() => setPageStep('review')}
          onRetry={loadCourses}
          onEditProfile={() => setProfilePopupOpen(true)}
        />
      )}

      {/* Review step */}
      {pageStep === 'review' && available && (
        <EnrollmentReviewStep
          childProfile={available.childProfile}
          selectedCourses={selectedCourses}
          submitting={enrolling}
          onEditProfile={() => setProfilePopupOpen(true)}
          onConfirm={handleConfirmEnroll}
          onBack={() => setPageStep('selection')}
        />
      )}

      {/* Success step */}
      {pageStep === 'success' && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CheckCircle sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Enrollment complete!
          </Typography>
          {successCourses.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center', mb: 2 }}>
              {successCourses.map((course) => (
                <Chip
                  key={course.id}
                  label={course.name}
                  color="success"
                  variant="outlined"
                  component={RouterLink}
                  to={`/student-courses/${course.id}?childId=${childId}`}
                  clickable
                />
              ))}
            </Box>
          )}
          {partialFailures.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
              <Typography variant="subtitle2" gutterBottom>
                Some courses could not be enrolled:
              </Typography>
              {partialFailures.map((f) => (
                <Typography key={f.courseId} variant="body2">
                  • {f.reason}
                </Typography>
              ))}
            </Alert>
          )}
          <Typography
            variant="body2"
            color="primary"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => navigate('/parent-dashboard')}
          >
            Return to dashboard
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ParentCourseRegistrationPage;
